import { VenueBasedEventAccessSerializer } from "@app-contexts/dashboard/features/events/data/types/event-accesses";
import {
  VenueBasedGiftCardDenominationSerializer,
  VenueBasedGiftCardSerializer,
} from "@app-contexts/dashboard/features/gift-cards/data/types";
import { VenueBasedMembershipGroupSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-groups";
import { VenueBasedMembershipLevelSerializer } from "@app-contexts/dashboard/features/memberships/data/types/membership-levels";
import {
  ProductAttributeSerializer,
  VenueBasedProductSerializer,
} from "@app-contexts/dashboard/features/products/data/types";
import { getFormattedDate } from "@shared/utils/dates/get-dates/get-dates-timezone";
import { t } from "@shared/utils/internationalization";
import { VenueBasedTicketTypeInventorySerializer } from "../../../data/types/inventory";
import { filterInventoriesByEventTicketTypes } from "../../../utils/venue-basket-utils";
import type { InventorySubItem } from "./types";

export function isEventAccess(
  item: unknown
): item is VenueBasedEventAccessSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "event");
}

export function isTicketType(
  item: unknown
): item is VenueBasedTicketTypeInventorySerializer {
  return (
    Object.prototype.hasOwnProperty.call(item ?? {}, "ticket_type") ||
    Object.prototype.hasOwnProperty.call(item ?? {}, "ticket_type_set")
  );
}

export function isProduct(item: unknown): item is VenueBasedProductSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "product_attributes");
}

export function isProductAttribute(
  item: unknown
): item is ProductAttributeSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "fees_pricing_info");
}

export function isMembership(
  item: unknown
): item is VenueBasedMembershipGroupSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "membership_levels");
}

export function isMembershipLevel(
  item: unknown
): item is VenueBasedMembershipLevelSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "active_members");
}

export function isGiftCard(
  item: unknown
): item is VenueBasedGiftCardSerializer {
  return Object.prototype.hasOwnProperty.call(item ?? {}, "denominations");
}

export function isGiftCardDenomination(
  item: unknown
): item is VenueBasedGiftCardDenominationSerializer {
  return (
    Object.prototype.hasOwnProperty.call(item ?? {}, "value") &&
    Object.prototype.hasOwnProperty.call(item ?? {}, "price") // add more unique fields here if any are added in future to the VenueBasedGiftCardDenominationSerializer
  );
}

// TODO: implement an alternate solution here for field access
// Example: generic typing of inventory items
export function hasAssignedSeating(item: unknown) {
  if (isEventAccess(item)) {
    return item.event.assigned_space;
  }

  if (isMembership(item)) {
    return item.reserved_seating_benefits?.[0]?.space;
  }

  return false;
}

// TODO: implement an alternate solution here for field access
// Example: generic typing of inventory items
export function getId(item: unknown) {
  if (isEventAccess(item)) {
    return item.event.id;
  }

  if (isProduct(item) || isMembership(item) || isGiftCard(item)) {
    return item.id;
  }

  return "Unknown id";
}

export function getName(item: unknown) {
  if (isEventAccess(item)) {
    return item.event.name;
  }

  if (isTicketType(item)) {
    return item.ticket_type.name;
  }

  if (isMembership(item)) {
    if (item.current_season_on_sale?.name) {
      return item.current_season_on_sale.name;
    }
    return item.name;
  }

  if (isGiftCard(item)) {
    return item.name;
  }

  if (isGiftCardDenomination(item)) {
    return `${item.value}`.concat(" ").concat(t("Gift card"));
  }

  if (isProduct(item) || isProductAttribute(item) || isMembershipLevel(item)) {
    return item.name;
  }

  return t("Unknown item");
}

export function getPrice(item: unknown) {
  if (isTicketType(item)) {
    return item.ticket_type.price ?? "TBD";
  }

  if (isProductAttribute(item)) {
    return String(item.price);
  }

  if (isMembershipLevel(item)) {
    return String(item.price);
  }

  if (isGiftCardDenomination(item)) {
    return item.price;
  }

  return t("Unknown price");
}

export function getFeesPricingInfo(item: unknown) {
  if (isTicketType(item)) {
    return item.ticket_type.fees_pricing_info ?? {};
  }

  if (isProductAttribute(item)) {
    return item.fees_pricing_info ?? {};
  }

  if (isMembershipLevel(item)) {
    return item.configured_fees_pricing_info ?? {};
  }

  return {};
}

export function getDescription(item: unknown) {
  let subline = "";

  if (isEventAccess(item)) {
    // If we have an indeterminate date...
    if (
      item.event.date_time_to_be_determined ||
      !item.event.starts_on ||
      !item.event.ends_on
    ) {
      subline = "TBD";
    }
    // ...otherwise
    else {
      subline = getFormattedDate({
        date: item.event.starts_on,
        timezone: item.event.timezone,
        format: "dddd MMM D, YYYY |",
        displayTime: true,
      });
    }
  }

  if (isProduct(item) && item.description) {
    subline = item.description;
  }

  if (isMembership(item)) {
    if (item.current_season_on_sale?.start_date) {
      subline = getFormattedDate({
        date: item.current_season_on_sale?.start_date,
        // null timezone to use browser timezone
        timezone: null,
        format: "ddd MMM D, YYYY",
      });
    } else if (item.description) {
      subline = item.description;
    }
  }

  // Strip HTML tags
  return subline.replace(/<\/?[^>]+(>|$)/g, "");
}

export function getSubItems(
  item?: unknown,
  ticketsAuthorizedByPassword?: number[]
): Array<InventorySubItem> {
  if (isEventAccess(item)) {
    // Filter inventories to only include ticket types that exist in the event's tickettype_set.
    // This filters out membership ticket types that should not be shown in the Tickets tab.
    const filteredInventories = filterInventoriesByEventTicketTypes(
      item.inventories,
      item.event.tickettype_set
    );

    // if a password only grants access to certain ticket types, filter the list to only include those that are authorized
    if (ticketsAuthorizedByPassword?.length) {
      return filteredInventories.filter((inventory) =>
        ticketsAuthorizedByPassword.includes(inventory.ticket_type.id)
      );
    }
    return filteredInventories;
  }

  if (isProduct(item)) {
    return item.product_attributes ?? [];
  }

  if (isMembership(item)) {
    return item.membership_levels;
  }

  if (isGiftCard(item)) {
    return item.denominations;
  }

  return [];
}
